import { useCallback, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMediaQuery } from 'react-responsive'
import {
  changeCommunicationPreferences,
  getCommunicationPreferencesKey,
  getCommunicationPreferences,
} from '../../../api-client/notifications'
import { Button } from '../../../components/button'
import { Mobile } from '../../../components/media'
import Modal from '../../../components/modal'
import Switch from '../../../components/switch'

const defaultPreferences = {
  letEmail: true,
  letPromotional: true,
  letSavedVehicleUpdate: true,
  letYourVehicleAuction: true,
  letYourVehicleBid: true,
  letBuyNowUpdate: true,
  letOfferUpdate: true,
}

export default function CommunicationPreferences({ onClose }) {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const [communicationPreferences, setCommunicationPreferences] = useState(defaultPreferences)

  const { isFetching } = useQuery({
    queryKey: getCommunicationPreferencesKey,
    queryFn: getCommunicationPreferences,
    onSuccess: (data) => setCommunicationPreferences(data.communicationPreference),
  })

  const { mutate, isLoading: mutating } = useMutation({
    mutationFn: changeCommunicationPreferences,
    onSuccess: onClose,
  })

  const handleSave = () => {
    mutate(communicationPreferences)
  }

  const handleSwitchChange = useCallback((name) => {
    setCommunicationPreferences((prev) => ({ ...prev, [name]: !prev[name] }))
  }, [])

  const isBusy = mutating || isFetching

  return (
    <Modal onClose={onClose} hideCloseBtn={isMobile}>
      <Mobile>
        <button className='flatBtn backToProfile' onClick={onClose}>
          &#60; Back to Profile
        </button>
      </Mobile>

      <div className='wrapper'>
        <h1 className='title'>Communication Preferences</h1>

        <section id='communicationMedium'>
          <h2 className='description'>Let us know how you want to hear from us.</h2>

          <div className='item'>
            <label htmlFor='emailPreference'>Email</label>
            <Switch
              id='emailPreference'
              name='letEmail'
              on={communicationPreferences.letEmail}
              onToggle={handleSwitchChange}
            />
          </div>
        </section>

        <section id='notificationTypes'>
          <h2 className='description'>Let us know what notifications you want.</h2>

          <div className='item'>
            <label htmlFor='promotionalEmails'>Promotional emails</label>
            <Switch
              id='promotionalEmails'
              name='letPromotional'
              on={communicationPreferences.letPromotional}
              onToggle={handleSwitchChange}
            />
          </div>

          <div className='item'>
            <label htmlFor='savedVehicleUpdates'>Saved vehicle updates</label>
            <Switch
              id='savedVehicleUpdates'
              name='letSavedVehicleUpdate'
              on={communicationPreferences.letSavedVehicleUpdate}
              onToggle={handleSwitchChange}
            />
          </div>

          <div className='item'>
            <label htmlFor='yourVehicleAuctions'>Your vehicle auctions</label>
            <Switch
              id='yourVehicleAuctions'
              name='letYourVehicleAuction'
              on={communicationPreferences.letYourVehicleAuction}
              onToggle={handleSwitchChange}
            />
          </div>

          <div className='item'>
            <label htmlFor='yourVehicleBids'>Your vehicle bids</label>
            <Switch
              id='yourVehicleBids'
              name='letYourVehicleBid'
              on={communicationPreferences.letYourVehicleBid}
              onToggle={handleSwitchChange}
            />
          </div>

          <div className='item'>
            <label htmlFor='buyNowUpdates'>Buy now updates</label>
            <Switch
              id='buyNowUpdates'
              name='letBuyNowUpdate'
              on={communicationPreferences.letBuyNowUpdate}
              onToggle={handleSwitchChange}
            />
          </div>

          <div className='item'>
            <label htmlFor='offers'>Offers</label>
            <Switch
              id='offers'
              name='letOfferUpdate'
              on={communicationPreferences.letOfferUpdate}
              onToggle={handleSwitchChange}
            />
          </div>
        </section>

        <Button className='saveBtn' disabled={isBusy} onClick={handleSave}>
          Save
        </Button>
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 1.6rem 2rem;
          background: #fff;
          border-radius: 1rem;
          font-size: 1.6rem;

          :global(.saveBtn) {
            display: block;
            margin: 4rem auto 0;
          }
        }

        .title {
          text-align: center;
          font-size: 3.2rem;
        }

        .description {
          margin: 1.5rem auto 0;
          text-align: center;
          font-size: 1.6rem;
          font-weight: 400;
        }

        .item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 2.1rem 0;

          label {
            font-weight: 700;
          }

          &:not(:last-child) {
            border-bottom: 0.1rem solid var(--separator-line-color);
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            height: 100%;
            padding: 5rem 1.35rem 6rem;

            :global(.saveBtn) {
              position: fixed;
              bottom: 2.1rem;
              left: 1.2rem;
              width: calc(100vw - 2.4rem);
            }
          }

          .backToProfile {
            margin: 1rem 0 0 1.3rem;
            color: #a5a8b9;
            line-height: 1.6rem;
            font-size: 1.2rem;
            font-weight: bold;
          }

          .title {
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          .description {
            margin: 0.2rem 0 3rem;
            line-height: 1.9rem;
            font-size: 1.4rem;
          }

          .item {
            padding: 2rem 0 2.45rem;

            label {
              font-size: 1.4rem;
            }
          }

          #notificationTypes {
            padding-bottom: 4rem;
          }
          #notificationTypes.description {
            margin: 2rem 0 2.7rem;
          }
        }
      `}</style>
    </Modal>
  )
}
